import { useEffect, useState } from "react";
import { api, money, percent, day, cabDateTime } from "../api.js";
import { SectionHead, PartialErrors, Empty, Pager } from "./common.jsx";
import RiskNotice, { RiskNoticeList } from "./smkor/RiskNotice.jsx";
import PnExplain, { ExplainCell } from "./smkor/PnExplain.jsx";
import { stopOutcome, explainProgress, addWorkDays } from "../../../lib/compliance/stop-outcome.js";
import { useReceiptNotices } from "./SubmitProgress.jsx";
import RiskMatrix from "./smkor/RiskMatrix.jsx";
import Money from "../components/Money.jsx";

// Ознаки позитивної податкової історії (p1..p9 у відповіді кабінету) — як у дашборді etax
const PPI = [
  "До 3 млн за місяць і до 500 тис. на покупця",
  "Вузька спеціалізація + D > 5 %",
  "Основні засоби > 5 млн + D > 2 %",
  "Земля ≥ 200 га або комунальна оренда ≥ 0,5 га",
  "≥ 5 працівників, зарплата понад 2 мінімальні",
  "Сплачено податків і ЄСВ за минулий рік > 10 млн",
  "Сплачено ПДВ > 1 млн за рік + D > 5 %",
  "Зупинені ПН закриті комісією",
  "36 місяців без порушень + D > 5 %",
];
const MONTHS = ["січ", "лют", "бер", "кві", "тра", "чер", "лип", "сер", "вер", "жов", "лис", "гру"];
const monthLabel = (rdate) => { const [y, m] = String(rdate).split("-"); return `${MONTHS[Number(m) - 1]} ${y?.slice(2)}`; };
const period = (dp, k) => (dp[`${k}_CACL_FROM`] ? `${day(dp[`${k}_CACL_FROM`])} – ${day(dp[`${k}_CACL_TO`])}` : "");

function Dp({ dp }) {
  if (!dp) return <Empty>Показників ще немає.</Empty>;
  const rows = [
    ["D", "Податкове навантаження (S / T)", percent(dp.D), ""],
    ["S", "Сплачені податки", money(dp.S), period(dp, "S")],
    ["T", "Обсяг постачання", money(dp.T), period(dp, "T")],
    ["Рнайб", "Найбільший місячний ПДВ за рік", money(dp.PM), period(dp, "PM")],
  ];
  return (
    <>
      <dl className="pairs pairs-wide">
        {rows.map(([k, t, v, p]) => <div key={k}><dt>{k} — {t}</dt><dd>{v}{p && <span className="muted"> · {p}</span>}</dd></div>)}
      </dl>
      <p className="notice">Рпоточ за останні 3 місяці: {["PP1", "PP2", "PP3"].map((k) => money(dp[k])).join(" · ")}{dp.REQUEST_DATE ? ` · розраховано ${cabDateTime(dp.REQUEST_DATE.replace("T", " "))}` : ""}</p>
    </>
  );
}

function Ppi({ ppi }) {
  if (!Array.isArray(ppi) || !ppi.length) return <Empty>Даних позитивної історії немає.</Empty>;
  const months = [...ppi].sort((a, b) => String(a.rdate).localeCompare(String(b.rdate)));
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label="Позитивна податкова історія">
      <table className="table-static ppi">
        <thead><tr><th>Ознака</th>{months.map((m) => <th key={m.rdate} className="center-cell">{monthLabel(m.rdate)}</th>)}</tr></thead>
        <tbody>
          {PPI.map((t, i) => (
            <tr key={i}>
              <td className="wrap-cell"><span className="muted">{i + 1}.</span> {t}</td>
              {months.map((m) => {
                const v = m[`p${i + 1}`];
                return <td key={m.rdate} className="center-cell">{v === 1 ? <span className="mark-yes" aria-label="виконується">●</span> : <span className="mark-no" aria-label="ні">·</span>}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Довільна таблиця з відповіді кабінету: колонки — ключі першого рядка
function Raw({ rows }) {
  const cols = Object.keys(rows[0] || {});
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label="Таблиця даних кабінету">
      <table className="table-static">
        <thead><tr>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c} className="wrap-cell">{r[c] == null ? "" : String(r[c])}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

// Зупинені ПН/РК: стан пояснення й одна дія (ExplainCell), головний результат розгляду (stopOutcome), скарга — у тому ж результаті
const TONE = { ok: "ok-text", bad: "error", warn: "warn-text", info: "" };
const mineOf = (drafts, code) => (drafts || []).filter((d) => d.invoices.some((x) => String(x.code) === String(code)));
// Доповнення, подане з eCabinet (прийняте або в дорозі), — найсвіжіше
const addedOf = (drafts, code) => mineOf(drafts, code).filter((d) => d.form === "J1307801" && ["accepted", "sent", "unknown", "sending"].includes(d.state))
  .sort((a, b) => String(b.fillDate).localeCompare(String(a.fillDate)))[0];
const STOP_PAGE = 10;
function Stopped({ list, companyId, company, today, drafts, reload }) {
  const [page, setPage] = useState(0);
  if (!Array.isArray(list)) return <Empty>Даних ще немає.</Empty>;
  if (!list.length) return <Empty>Зупинених ПН/РК за два роки немає.</Empty>;
  const pages = Math.ceil(list.length / STOP_PAGE), cur = Math.min(page, pages - 1);
  return (
    <>
    <div className="table-wrap" tabIndex={0} role="region" aria-label="Зупинені ПН/РК">
      <table className="table-static stopped">
        <thead><tr><th>ПН</th><th>Покупець</th><th className="num">ПДВ</th><th>Пояснення</th><th>Рішення комісії</th></tr></thead>
        <tbody>
          {list.slice(cur * STOP_PAGE, (cur + 1) * STOP_PAGE).map((s) => {
            const open = Number(s.status) === 1;
            let out = stopOutcome(s, today);
            // Доповнення вже подано з eCabinet, а список ДПС ще його не показує (запит або «на розгляді») — показуємо з наших даних
            const added = addedOf(drafts, s.code);
            if (added && out && (out.tone === "warn" || out.title === "На розгляді комісії")) {
              const due = addWorkDays(added.fillDate, 5);
              out = { tone: "info", title: "Доповнення подано — на розгляді", detail: `доповнення ${day(added.fillDate)}${added.state === "accepted" ? "" : " (чекаємо квитанцію)"}; рішення — до ${day(due)}` };
            }
            const prog = open && drafts ? explainProgress({ stop: s, drafts: mineOf(drafts, s.code), today }) : null;
            return (
              <tr key={s.ijcode}>
                <td>№ {s.nmr} від {day(s.crtdate)}{!open && <><br /><span className="muted small">{s.statusName}</span></>}</td>
                <td className="wrap-cell">{s.buyer}{s.cpTin ? <><br /><span className="muted">{s.cpTin}</span></> : null}</td>
                <td className="num"><Money value={s.vat} /></td>
                <td className="wrap-cell">{open && drafts ? <ExplainCell companyId={companyId} company={company} stop={s} drafts={drafts} today={today} onChanged={reload} />
                  : (s.det || []).some((d) => d.hdate) ? <span className="muted">подано</span> : ""}</td>
                <td className="wrap-cell stop-decision">{out ? <><b className={TONE[out.tone]}>{out.title}</b><br /><span className="muted small">{out.detail}</span>
                  {out.tone === "warn" && <><br /><a className="small" href={`#/c/${companyId}/correspondence`}>запит — у Листуванні</a></>}</>
                  : open ? <span className="muted small">{prog?.status.tone === "ok" || prog?.action?.kind === "check" ? "після прийняття пояснення ДПС" : "спершу подайте пояснення"}</span> : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {pages > 1 && <Pager page={cur} pages={pages} total={list.length} unit="ПН/РК" go={setPage} />}
    </>
  );
}

// Статус ризиковості вгорі розділу: ризиковий / не ризиковий, з якого дня й за яким критерієм; лічильники зупинених ПН
const rv = (r, k) => r?.[k] ?? r?.[k.toUpperCase()];
const activeOf = (risk, today) => (Array.isArray(risk) ? risk.filter((r) => !rv(r, "dateEnd") || String(rv(r, "dateEnd")).slice(0, 10) >= today) : null);
// Лічильники — за тим самим станом, що й колонка «Пояснення» (explainProgress: дані ДПС + подання eCabinet)
function RiskStatus({ risk, stopped, today, drafts }) {
  const active = activeOf(risk, today);
  const open = Array.isArray(stopped) ? stopped.filter((s) => Number(s.status) === 1) : [];
  const prog = drafts ? open.map((s) => ({ s, p: explainProgress({ stop: s, drafts: mineOf(drafts, s.code), today }) })) : [];
  const counts = [["зупинено ПН", open.length],
    ["без пояснення", prog.filter(({ p }) => p.status.tone === "bad").length],
    ["чекають квитанцію", prog.filter(({ p }) => p.action?.kind === "check").length],
    ["запит доповнення без відповіді", prog.filter(({ s }) => stopOutcome(s, today)?.tone === "warn" && !addedOf(drafts, s.code)).length]].filter(([, n]) => n > 0);
  const tail = counts.length > 0 && <p className="smkor-status-counts">{counts.map(([t, n]) => <span key={t}><b>{n}</b> {t}</span>)}</p>;
  if (active === null) return <section className="card smkor-status"><p className="muted">Статус ризиковості невідомий — оновіть дані кабінету.</p>{tail}</section>;
  // Критерії — у тій самій смузі: чинні, а для не ризикового — ті, що вже не діють (історія)
  if (!active.length) return <section className="card smkor-status smkor-status-ok"><p className="smkor-status-title">Не ризиковий платник</p>
    <p className="muted small">Чинних критеріїв ризиковості в кабінеті немає.</p>{tail}
    {risk.length > 0 && <details className="small"><summary className="muted">Критерії, що вже не діють ({risk.length})</summary><RiskCriteria risk={risk} today={today} /></details>}</section>;
  const first = [...active].sort((a, b) => String(rv(a, "dateBegin")).localeCompare(String(rv(b, "dateBegin"))))[0];
  return (
    <section className="card smkor-status smkor-status-bad" role="status">
      <p className="smkor-status-title">Ризиковий платник</p>
      <p>з {day(rv(first, "dateBegin"))}. Кожна нова ПН зупинятиметься, поки статус діє.</p>
      <RiskCriteria risk={risk} today={today} />
      {tail}
    </section>
  );
}

// Критерії ризиковості: назва й стан, дати окремим рядком, повний текст критерію — під ними
function RiskCriteria({ risk, today }) {
  if (!Array.isArray(risk)) return <Empty>Даних ще немає.</Empty>;
  if (!risk.length) return <p className="ok-text">Платник не відповідає критеріям ризиковості.</p>;
  return (
    <ul className="risk-crit-list">
      {risk.map((r, i) => {
        const end = rv(r, "dateEnd"), active = !end || String(end).slice(0, 10) >= today;
        return (
          <li key={i} className="risk-crit">
            <div className="risk-crit-head"><b>{rv(r, "typName") || `Критерій ${rv(r, "typ") ?? ""}`}</b>
              <span className={active ? "badge risk-badge-bad" : "badge ok"}>{active ? "діє" : `не діє з ${day(end)}`}</span></div>
            <p className="muted small">Застосовано з {day(rv(r, "dateBegin")) || "—"}{rv(r, "dateUse") && day(rv(r, "dateUse")) !== day(rv(r, "dateBegin")) ? ` · використано ${day(rv(r, "dateUse"))}` : ""}{end ? ` · до ${day(end)}` : " · строк не встановлено"}{rv(r, "typ") != null ? ` · код ${rv(r, "typ")}` : ""}</p>
            {rv(r, "typFullName") && <p className="small">{rv(r, "typFullName")}</p>}
          </li>
        );
      })}
    </ul>
  );
}

// Дорожня карта: що робити зараз (lib/compliance/smkor-plan.js). Перший рівень — кроки; норми — за кнопкою
const LINKS = { correspondence: "Листування з ДПС", primary: "Первинні документи", sea: "СЕА", erpn: "ПН/РК", "smkor/notice": "Скласти J1314901", opp: "20-ОПП", customs: "Митні декларації" };
function Plan({ plan, companyId }) {
  const [basis, setBasis] = useState(false);
  if (!plan || plan.status === "unknown") return null;
  if (plan.status === "ok") return <section className="card smkor-plan"><h3>Що робити</h3><p className="ok-text">Статусу ризикового платника й зупинених ПН без пояснень немає — нічого робити не потрібно.</p></section>;
  return (
    <section className={`card smkor-plan${plan.status === "risky" ? " smkor-plan-risk" : ""}`} aria-labelledby="smkor-plan-h">
      <h3 id="smkor-plan-h">Що робити зараз</h3>
      {plan.sections.map((sec) => (
        <div key={sec.key} className="smkor-plan-sec">
          <p><b>{sec.title}.</b> {sec.why}</p>
          <ol>
            {sec.steps.map((st, i) => (
              <li key={i} className={st.warn ? "warn" : undefined}>{st.text}{st.link && <> <a href={`#/c/${companyId}/${st.link}`}>{LINKS[st.link]} →</a></>}</li>
            ))}
          </ol>
          {sec.until && <p className="muted small">{sec.until}</p>}
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" aria-expanded={basis} onClick={() => setBasis(!basis)}>{basis ? "Сховати підстави" : "Показати підстави"}</button>
      {basis && <ul className="small smkor-basis">{plan.basis.map((b, i) => <li key={i}>{b}</li>)}</ul>}
      {basis && plan.pmax && (
        <div className="table-wrap" tabIndex={0} role="region" aria-label="Умова Рмакс за місяцями">
          <table className="table-static">
            <caption className="muted small">Умова Рмакс для автоматичної реєстрації: ПДВ прийнятих на реєстрацію ПН за місяцем і межі Рмакс (дані eCabinet і показник Рнайб кабінету)</caption>
            <thead><tr><th>Місяць</th><th className="num">Прийнято, ПДВ</th><th className="num">Рмакс не менше</th><th className="num">Рмакс не більше</th><th>Висновок</th></tr></thead>
            <tbody>{plan.pmax.months.map((r) => (
              <tr key={r.month}><td>{r.month.split("-").reverse().join(".")}</td><td className="num">{money(r.accepted)}</td><td className="num">{r.pmaxLow ? money(r.pmaxLow) : "—"}</td>
                <td className="num">{r.pmax != null ? money(r.pmax) : "невідомо"}</td>
                <td>{r.verdict === "over" ? <span className="error">перевищує</span> : r.verdict === "ok" ? <span className="ok-text">виконується</span> : "невідомо"}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <p className="muted small">Строки пораховано від сьогодні за робочими днями (пн–пт). Реєстрацію ПН комісія не гарантує — рішення за нею.</p>
    </section>
  );
}

export default function Smkor({ companyId, company, sub, res, version, onSync }) {
  if (/^explain(\/|$)/.test(sub || "")) return <PnExplain companyId={companyId} company={company} sub={sub} version={version} />;
  if (/^notice(\/|$)/.test(sub || "")) return <RiskNotice companyId={companyId} company={company} sub={sub} version={version} />;
  return <SmkorMain companyId={companyId} company={company} res={res} version={version} onSync={onSync} />;
}

function SmkorMain({ companyId, company, res, version, onSync }) {
  const [d, setD] = useState(null), [drafts, setDrafts] = useState(null);
  const reloadDrafts = () => api(`/api/companies/${companyId}/pn-explain`).then((x) => setDrafts(x.drafts), () => setDrafts([]));
  useEffect(() => { api(`/api/companies/${companyId}/smkor`).then(setD); reloadDrafts(); }, [companyId, version]);
  // Подання в дорозі (завантаження, надсилання, очікування квитанції — її перевіряє сервер): список оновлюємо раз на 30 с
  const live = (drafts || []).some((x) => ["creating", "signing", "sending", "sent", "unknown"].includes(x.state));
  useEffect(() => { if (!live) return; const t = setInterval(reloadDrafts, 30000); return () => clearInterval(t); }, [live, companyId]);
  useReceiptNotices(drafts, (x) => `${x.form === "J1307801" ? "доповнення" : "пояснення"} до ПН № ${x.invoices?.[0]?.nmr ?? ""}`);
  if (!d) return <><SectionHead title="СМКОР" res={res} onSync={onSync} /><p className="muted">Завантаження…</p></>;
  const risk = d.risk?.data;
  const tables = d.tables?.data;
  const today = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Kyiv" });
  return (
    <>
      <SectionHead title="СМКОР" res={res} onSync={onSync} />
      <PartialErrors res={res} />
      <RiskStatus risk={risk} stopped={d.stopped?.data} today={today} drafts={drafts} />
      <Plan plan={d.plan} companyId={companyId} />
      {d.plan?.status === "risky" && <RiskNoticeList companyId={companyId} company={company} compact />}
      <section className="card">
        <h3>Зупинені ПН/РК</h3>
        <Stopped list={d.stopped?.data} companyId={companyId} company={company} today={today} drafts={drafts} reload={reloadDrafts} />
      </section>
      <RiskMatrix companyId={companyId} version={version} />
      <section className="card">
        <h3>Показники D і P</h3>
        <Dp dp={d.dp?.data} />
      </section>
      <section className="card">
        <h3>Позитивна податкова історія</h3>
        <Ppi ppi={d.ppi?.data} />
      </section>
      <section className="card">
        <h3>Враховані таблиці даних платника</h3>
        {!Array.isArray(tables) ? <Empty>Даних ще немає.</Empty> : !tables.length ? <Empty>Врахованих таблиць немає.</Empty> : <Raw rows={tables} />}
      </section>
    </>
  );
}
